import { useState } from "react";
import { toast } from "sonner";
import { CircleNotch, CheckCircle, PencilSimple, Trash, Receipt, Storefront } from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/api";

const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

export function RuleRow({ rule, editing, categories, onEdit, onCancel, onSave, onDelete }) {
  const [form, setForm] = useState({ match: rule.match, category: rule.category });
  const [saving, setSaving] = useState(false);
  const options = categories?.[rule.txn_type] || [];
  const isItem = rule.kind === "item";
  const cell = { borderColor: "var(--bk-border)" };

  const save = async () => {
    if (!form.match.trim()) return toast.error("The matching text can't be empty.");
    if (!form.category) return toast.error("Pick a category first.");
    setSaving(true);
    try { await onSave(form); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update that rule."); } finally { setSaving(false); }
  };

  return (
    <tr className="border-b last:border-b-0 align-top" style={cell} data-testid={`receipt-rule-row-${rule.id}`}>
      <td className="py-4 pl-6 pr-4 max-w-[320px]">
        {editing ? (
          <input value={form.match} onChange={(e) => setForm((s) => ({ ...s, match: e.target.value }))} data-testid={`receipt-rule-match-input-${rule.id}`} className="w-full rounded-md border px-3 py-2 text-sm" style={cell} />
        ) : (
          <>
            <p className="font-semibold break-words" style={{ color: "var(--bk-text)" }} data-testid={`receipt-rule-match-${rule.id}`}>{rule.match}</p>
            <p className="text-xs mt-1 inline-flex items-center gap-1" style={{ color: "var(--bk-text-2)" }}>
              {isItem ? <Receipt size={11} weight="bold" /> : <Storefront size={11} weight="bold" />}
              {isItem ? (rule.merchant ? `Receipt line · ${rule.merchant}` : "Receipt line") : `Vendor · ${rule.txn_type === "income" ? "income" : "expense"}${rule.auto_apply_silently ? " · auto-applies" : " · still confirming"}`}
            </p>
          </>
        )}
      </td>
      <td className="py-4 pr-4">
        {editing ? (
          <select value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} data-testid={`receipt-rule-category-select-${rule.id}`} className="w-full min-w-[200px] rounded-md border px-3 py-2 text-sm bg-white" style={cell}>
            <option value="">Pick a category</option>
            {options.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <span className="inline-flex px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid={`receipt-rule-category-${rule.id}`}>{rule.category}</span>
        )}
      </td>
      <td className="py-4 pr-4 whitespace-nowrap" style={{ color: "var(--bk-text)" }} data-testid={`receipt-rule-business-${rule.id}`}>{rule.business_name || "—"}</td>
      <td className="py-4 pr-4 whitespace-nowrap" style={{ color: "var(--bk-text-2)" }} data-testid={`receipt-rule-learned-${rule.id}`}>
        <p>{fmtDate(rule.learned_at)}</p>
        <p className="text-[11px] mt-0.5">{rule.source_label}</p>
      </td>
      <td className="py-4 pr-4 whitespace-nowrap" style={{ color: "var(--bk-text-2)" }} data-testid={`receipt-rule-used-${rule.id}`}>
        {isItem ? "On matching receipt lines" : `${rule.times_applied}× · ${rule.last_used_at ? fmtDate(rule.last_used_at) : "not used yet"}`}
      </td>
      <td className="py-4 pr-6">
        <div className="flex justify-end gap-2">
          {editing ? (
            <>
              <button onClick={onCancel} data-testid={`receipt-rule-cancel-${rule.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold" style={{ ...cell, color: "var(--bk-text)" }}>Cancel</button>
              <button onClick={save} disabled={saving} data-testid={`receipt-rule-save-${rule.id}`} className="bk-btn-primary px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                {saving ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} weight="fill" />} Save
              </button>
            </>
          ) : (
            <>
              <button onClick={onEdit} data-testid={`receipt-rule-edit-${rule.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5" style={{ ...cell, color: "var(--bk-text)" }}><PencilSimple size={12} weight="bold" /> Edit</button>
              <button onClick={onDelete} data-testid={`receipt-rule-delete-${rule.id}`} className="px-3 py-2 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5" style={{ borderColor: "rgba(178,59,31,0.2)", color: "#B23B1F" }}><Trash size={12} weight="bold" /> Delete</button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
